/**
 * Theme selection.
 *
 * Three states rather than two: `system` follows the phone, which is what most
 * people want without being asked, while `light` and `dark` override it. The
 * override is stored per-device, not per-seat — it is a property of the screen
 * you are looking at, not of the game.
 */

export type Theme = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

const KEY = 'risk.theme';

export function storedTheme(): Theme {
  try {
    const value = localStorage.getItem(KEY);
    return value === 'light' || value === 'dark' ? value : 'system';
  } catch {
    // Private browsing or blocked storage: follow the system and move on.
    return 'system';
  }
}

export function systemTheme(): ResolvedTheme {
  return typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

export function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? systemTheme() : theme;
}

/**
 * `system` removes the attribute entirely rather than stamping a value, so the
 * media query in the stylesheet governs and the choice keeps tracking the
 * phone if it switches at dusk.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  try {
    if (theme === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, theme);
  } catch {
    // The theme still applies for this session; it just will not be remembered.
  }
}

export function nextTheme(theme: Theme): Theme {
  return theme === 'system' ? 'light' : theme === 'light' ? 'dark' : 'system';
}

export const THEME_LABEL: Readonly<Record<Theme, string>> = {
  system: 'Match the phone',
  light: 'Light',
  dark: 'Dark',
};

export const THEME_GLYPH: Readonly<Record<Theme, string>> = {
  system: '◐',
  light: '☀',
  dark: '☾',
};

/** Notifies when the system preference changes, for `system` mode. */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof matchMedia !== 'function') return () => {};
  const query = matchMedia('(prefers-color-scheme: dark)');
  query.addEventListener('change', onChange);
  return () => query.removeEventListener('change', onChange);
}
